import { useEffect, useState, useCallback } from "react";
import { Eye, TrendingUp, TrendingDown, Minus, Clock, AlertCircle, RefreshCw, ChevronDown, Flag, Loader2 } from "lucide-react";
import { dashboardService, type MonitoredStudent } from "@/services/dashboardService";
import { apiPost, apiPatch } from "@/services/apiClient";
import { StudentProfileModal } from "./StudentProfileModal";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { safeLog } from '@/utils/safeLog';

type Presence = 'online' | 'away' | 'offline';

function getPresence(lastActive: string | null): Presence {
  if (!lastActive) return 'offline';
  const diff = (Date.now() - new Date(lastActive).getTime()) / 1000 / 60; // minutes
  if (diff < 15) return 'online';
  if (diff < 60) return 'away';
  return 'offline';
}

const PRESENCE_STYLES: Record<Presence, { dot: string; label: string }> = {
  online:  { dot: 'bg-emerald-500', label: 'Online' },
  away:    { dot: 'bg-amber-400',   label: 'Away' },
  offline: { dot: 'bg-slate-300',   label: 'Offline' },
};

const getRiskLevel = (score: number): "low" | "medium" | "high" => {
  if (score < 40) return "low";
  if (score < 70) return "medium";
  return "high";
};

const riskStyles = {
  high:   { bar: "bg-red-500",    light: "bg-red-100",    text: "text-red-600",    badge: "bg-red-100 text-red-700 border-red-200",    label: "High Risk" },
  medium: { bar: "bg-amber-500",  light: "bg-amber-100",  text: "text-amber-600",  badge: "bg-amber-100 text-amber-700 border-amber-200", label: "Medium Risk" },
  low:    { bar: "bg-emerald-500",light: "bg-emerald-100",text: "text-emerald-600",badge: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Low Risk" },
};

type SortKey = 'riskScore' | 'name' | 'alertsThisMonth' | 'lastActive';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'riskScore', label: 'Risk Score' },
  { value: 'name', label: 'Name' },
  { value: 'alertsThisMonth', label: 'Alerts This Month' },
  { value: 'lastActive', label: 'Last Active' },
];

export function StudentsMonitored() {
  const { dm } = useTeacherTheme();
  const [students, setStudents] = useState<MonitoredStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey>('riskScore');
  const [sortOpen, setSortOpen] = useState(false);
  const [filterRisk, setFilterRisk] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [selectedStudent, setSelectedStudent] = useState<MonitoredStudent | null>(null);
  const [flaggingId, setFlaggingId] = useState<string | null>(null);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getMonitoredStudents(page, 20);
      setStudents(response.data);
      setTotalPages(response.totalPages);
    } catch (error) {
      safeLog.error("Failed to load students", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'No activity';
    try {
      const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
      if (seconds < 60) return 'Just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    } catch { return 'Unknown'; }
  };

  const handleFlagForReview = async (student: MonitoredStudent, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setFlaggingId(student.id);
      if (student.isFlagged && student.flagAlertId) {
        await apiPatch(`/alerts/${student.flagAlertId}/status`, { status: 'dismissed' });
      } else {
        await apiPost('/alerts', {
          studentId: student.id,
          type: 'teacher_flag',
          severity: student.riskScore >= 70 ? 'high' : student.riskScore >= 40 ? 'medium' : 'low',
          title: `Teacher Flag: ${student.name} flagged for review`,
          description: `Manually flagged by teacher. Current risk score: ${student.riskScore}%.`,
        });
      }
      await fetchStudents();
    } catch (err) {
      safeLog.error('Failed to toggle flag for student', err);
    } finally {
      setFlaggingId(null);
    }
  };

  // Sort + filter client-side
  const processed = [...students]
    .filter(s => filterRisk === 'all' || getRiskLevel(s.riskScore) === filterRisk)
    .sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'alertsThisMonth': return (b.alertsThisMonth ?? 0) - (a.alertsThisMonth ?? 0);
        case 'lastActive': {
          const aT = a.lastActive ? new Date(a.lastActive).getTime() : 0;
          const bT = b.lastActive ? new Date(b.lastActive).getTime() : 0;
          return bT - aT;
        }
        default: return b.riskScore - a.riskScore;
      }
    });

  // Render inline profile view when a student is selected
  if (selectedStudent) {
    return (
      <StudentProfileModal
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">

      {/* Toolbar */}
      <div className={`border rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-dm={dm}>
        <div className="flex items-center gap-2">
          <Eye className={`h-4 w-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`} />
          <span className={`text-sm font-semibold ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
            {processed.length} student{processed.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Risk filter */}
          <div className="flex items-center gap-1">
            {(['all', 'high', 'medium', 'low'] as const).map(level => (
              <button
                key={level}
                onClick={() => setFilterRisk(level)}
                className={`btn-glass btn-glass-pill ${filterRisk === level ? 'btn-glass-blue' : ''}`}
              >
                {level === 'all' ? 'All' : level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(o => !o)}
              className="btn-glass btn-glass-pill flex items-center gap-1.5"
            >
              Sort: <span className="font-bold">{SORT_OPTIONS.find(o => o.value === sortBy)?.label}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {sortOpen && (
              <div className={`absolute right-0 mt-1 w-44 border rounded-xl shadow-lg z-10 overflow-hidden ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-xs transition ${sortBy === opt.value ? 'font-semibold' : ''} ${dm ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      color: sortBy === opt.value ? '#2563eb' : (dm ? '#e2e8f0' : '#000000')
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={fetchStudents}
            disabled={loading}
            className="btn-glass btn-glass-pill flex items-center gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Student cards */}
      {loading && students.length === 0 ? (
        <div className="flex justify-center items-center py-20 text-slate-400">
          <RefreshCw className="animate-spin h-6 w-6 mr-2" />
          <span className="text-sm">Loading students...</span>
        </div>
      ) : processed.length === 0 ? (
        <div className={`text-center py-16 rounded-xl border ${dm ? 'bg-slate-800 border-slate-700 text-slate-500' : 'bg-white border-slate-200 text-slate-400'}`}>
          <p className="text-sm">No students match the selected filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {processed.map(student => {
            const level = getRiskLevel(student.riskScore);
            const styles = riskStyles[level];
            const initials = student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
            const isFlagged = !!student.isFlagged;
            const presence = getPresence(student.lastActive);
            const presenceStyle = PRESENCE_STYLES[presence];

            return (
              <div
                key={student.id}
                className={`border rounded-xl p-5 shadow-sm hover:shadow-md transition cursor-pointer ${dm ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200'}`}
                onClick={() => setSelectedStudent(student)}
              >
                <div className="flex gap-4">
                  {/* Avatar + presence dot */}
                  <div className="relative flex-shrink-0">
                    <div className={`flex items-center justify-center w-11 h-11 rounded-full ${styles.light}`}>
                      <span className={`text-sm font-bold ${styles.text}`}>{initials}</span>
                    </div>
                    
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <h3 className={`text-base font-semibold truncate ${dm ? 'text-slate-100' : 'text-slate-900'}`}>{student.name}</h3>
                        <p className={`text-xs truncate ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{student.email || ''}</p>
                      </div>
                      <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles.badge}`}>
                        {styles.label}
                      </span>
                    </div>

                    {/* Risk bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Risk Score</span>
                        <span className={`text-sm font-bold ${styles.text}`}>{student.riskScore}%</span>
                      </div>
                      <div className={`w-full rounded-full h-2 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                        <div
                          className={`h-2 rounded-full ${styles.bar} transition-all`}
                          style={{ width: `${Math.min(student.riskScore, 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className={`flex items-center gap-4 mb-3 text-xs flex-wrap ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                      <span className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-slate-400" />
                        {student.alertsThisMonth ?? 0} alert{(student.alertsThisMonth ?? 0) !== 1 ? 's' : ''} this month
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-slate-400" />
                        {formatTimeAgo(student.lastActive)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className={`h-2 w-2 rounded-full ${presenceStyle.dot}`} />
                        <span className={presence === 'online' ? 'text-emerald-600 font-medium' : presence === 'away' ? 'text-amber-600' : 'text-slate-400'}>
                          {presenceStyle.label}
                        </span>
                      </span>
                      {/* Trend indicator */}
                      <span className={`flex items-center gap-0.5 font-medium ${
                        level === 'high' ? 'text-red-500' : level === 'medium' ? 'text-amber-500' : 'text-emerald-500'
                      }`}>
                        {level === 'high' ? <TrendingUp className="h-3 w-3" /> : level === 'medium' ? <Minus className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {level === 'high' ? 'Increasing' : level === 'medium' ? 'Stable' : 'Improving'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setSelectedStudent(student)}
                        className="btn-glass btn-glass-pill"
                      >
                        View Profile
                      </button>
                      <button
                        onClick={(e) => handleFlagForReview(student, e)}
                        disabled={flaggingId === student.id}
                        className={`btn-glass btn-glass-pill flex items-center gap-1.5 disabled:opacity-60 ${
                          isFlagged ? 'btn-glass-emerald' : 'btn-glass-danger'
                        }`}
                      >
                        {flaggingId === student.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : isFlagged ? (
                          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                            <line x1="4" y1="22" x2="4" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        ) : (
                          <Flag className="h-3 w-3" />
                        )}
                        {isFlagged ? 'Flagged' : 'Flag for Review'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between border rounded-xl px-4 py-3 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading} className="btn-glass btn-glass-pill">
              Previous
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="btn-glass btn-glass-pill">
              Next
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
